import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import { PrivacyPage, TermsPage } from "@/pages/Legal";
import { BlogIndex, BlogPost } from "@/pages/Blog";
import SitterChecklist from "@/pages/SitterChecklist";
import { LoginPage, SignupPage, ForgotPage, ResetPage, AuthCallback } from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Today from "@/pages/Today";
import Pets from "@/pages/Pets";
import PetDetail from "@/pages/PetDetail";
import PetEdit from "@/pages/PetEdit";
import EmergencyCard from "@/pages/EmergencyCard";
import Share from "@/pages/Share";
import Reminders from "@/pages/Reminders";
import Settings from "@/pages/Settings";
import Guide from "@/pages/Guide";
import Join from "@/pages/Join";
import SitterView from "@/pages/SitterView";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Admin from "@/pages/Admin";
import Shopping from "@/pages/Shopping";

export function Loading() {
  return (
    <div className="min-h-screen grid place-items-center" data-testid="page-loading">
      <div className="h-10 w-10 rounded-full border-4 border-amber-200 border-t-amber-500 animate-spin" />
    </div>
  );
}

function Protected({ children, needHousehold = true }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) {
    localStorage.setItem("pl_next", loc.pathname + loc.search);
    return <Navigate to="/login" replace />;
  }
  if (needHousehold && !user.household_id) return <Navigate to="/onboarding" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const app = (el) => <Protected><AppShell>{el}</AppShell></Protected>;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/pet-sitter-checklist" element={<SitterChecklist />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot" element={<ForgotPage />} />
      <Route path="/reset/:token" element={<ResetPage />} />
      <Route path="/join/:token" element={<Join />} />
      <Route path="/s/:token" element={<SitterView />} />
      <Route path="/onboarding" element={<Protected needHousehold={false}><Onboarding /></Protected>} />
      <Route path="/app" element={app(<Today />)} />
      <Route path="/app/pets" element={app(<Pets />)} />
      <Route path="/app/pets/new" element={app(<PetEdit />)} />
      <Route path="/app/pets/:id" element={app(<PetDetail />)} />
      <Route path="/app/pets/:id/edit" element={app(<PetEdit />)} />
      <Route path="/app/emergency" element={app(<EmergencyCard />)} />
      <Route path="/app/share" element={app(<Share />)} />
      <Route path="/app/reminders" element={app(<Reminders />)} />
      <Route path="/app/shopping" element={app(<Shopping />)} />
      <Route path="/app/settings" element={app(<Settings />)} />
      <Route path="/app/guide" element={<Protected><Guide /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/admin" element={<Protected needHousehold={false}><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-center" offset="76px" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
